import { useState, useEffect } from "react";
import { Plus, Trash2, Star, Layout, FileText, Dumbbell, Leaf, Globe, Settings, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, Field, Card, ImageUploader, RichTextEditor } from "./AdminShared";
import { Phone, MapPin } from "lucide-react";

function SortableServiceItem({ id, s, i, token, setArr, delArr }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <details className="border border-white/5 rounded-lg group bg-gg-black">
        <summary className="px-4 py-3 cursor-pointer flex items-center gap-2 text-sm text-white hover:text-gg-red transition-colors">
          <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-600 hover:text-zinc-400 touch-none"><GripVertical size={16} /></span>
          <span className="font-heading uppercase tracking-wider text-xs flex-1">{s.name || `Service ${i + 1}`}</span>
          <Trash2 size={14} className="text-zinc-600 hover:text-red-500 cursor-pointer shrink-0" onClick={(e) => { e.preventDefault(); delArr("services", i); }} />
        </summary>
        <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
          <Field label="Name" value={s.name} onChange={(v) => setArr("services", i, "name", v)} />
          <Field label="Short Description" value={s.desc} onChange={(v) => setArr("services", i, "desc", v)} />
          <Field label="Detailed Description" value={s.detail} onChange={(v) => setArr("services", i, "detail", v)} rows={2} />
          <ImageUploader token={token} currentUrl={s.image} onUploaded={(url) => setArr("services", i, "image", url)} label="Service Image (optional)" aspect={3/4} />
        </div>
      </details>
    </div>
  );
}

function SortableQualItem({ id, q, i, setArr, delArr }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <details className="border border-white/5 rounded-lg bg-gg-black">
        <summary className="px-4 py-3 cursor-pointer flex items-center gap-2 text-sm text-white hover:text-gg-red">
          <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-zinc-600 hover:text-zinc-400 touch-none"><GripVertical size={16} /></span>
          <span className="font-heading uppercase tracking-wider text-xs flex-1">{q.title || `Qualification ${i + 1}`}</span>
          <Trash2 size={14} className="text-zinc-600 hover:text-red-500 shrink-0" onClick={(e) => { e.preventDefault(); delArr("qualifications", i); }} />
        </summary>
        <div className="px-4 pb-4 space-y-3 border-t border-white/5 pt-3">
          <Field label="Title" value={q.title} onChange={(v) => setArr("qualifications", i, "title", v)} />
          <Field label="Description" value={q.desc} onChange={(v) => setArr("qualifications", i, "desc", v)} rows={2} />
          <Field label="Detail (modal popup)" value={q.detail} onChange={(v) => setArr("qualifications", i, "detail", v)} rows={2} />
        </div>
      </details>
    </div>
  );
}

export default function AdminContentTab({ token }) {
  const [content, setContent] = useState(null);
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState("");
  const [publishing, setPublishing] = useState("");
  const [section, setSection] = useState("hero");

  useEffect(() => {
    api(token).get("/content/draft").then((r) => { setContent(r.data.content); setMeta(r.data.meta); }).catch(() => toast.error("Failed to load"));
  }, [token]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const save = async (s, silent) => {
    setSaving(s);
    try { await api(token).put(`/content/${s}`, content[s]); setMeta((p) => ({ ...p, [s]: { ...p[s], has_draft: true } })); if (!silent) toast.success("Draft saved!"); } catch { toast.error("Save failed"); }
    setSaving("");
  };

  const publish = async (s) => {
    setPublishing(s);
    try {
      await api(token).put(`/content/${s}`, content[s]);
      await api(token).post(`/content/${s}/publish`);
      setMeta((p) => ({ ...p, [s]: { ...p[s], has_draft: false, is_published: true, scheduled_publish_at: null } }));
      toast.success("Published live!");
    } catch { toast.error("Publish failed"); }
    setPublishing("");
  };

  const updateMeta = (s, updates) => setMeta((p) => ({ ...p, [s]: { ...p[s], ...updates } }));

  const set = (sec, field, val) => setContent((p) => ({ ...p, [sec]: { ...p[sec], [field]: val } }));
  const setArr = (sec, idx, field, val) => setContent((p) => { const a = [...p[sec]]; a[idx] = { ...a[idx], [field]: val }; return { ...p, [sec]: a }; });
  const addArr = (sec, item) => setContent((p) => ({ ...p, [sec]: [...p[sec], item] }));
  const delArr = (sec, idx) => setContent((p) => ({ ...p, [sec]: p[sec].filter((_, i) => i !== idx) }));

  if (!content) return <p className="text-zinc-500 py-10 text-center">Loading CMS...</p>;

  const handleDragEnd = (sec) => (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const items = content[sec];
      const oldIdx = items.findIndex((_, i) => `${sec}-${i}` === active.id);
      const newIdx = items.findIndex((_, i) => `${sec}-${i}` === over.id);
      setContent((p) => ({ ...p, [sec]: arrayMove(p[sec], oldIdx, newIdx) }));
    }
  };

  const nav = [
    { id: "hero", label: "Hero", icon: Layout },
    { id: "about", label: "About", icon: FileText },
    { id: "services", label: "Services", icon: Dumbbell },
    { id: "qualifications", label: "Credentials", icon: Star },
    { id: "nutrition", label: "Nutrition", icon: Leaf },
    { id: "online_training", label: "Online Training", icon: Globe },
    { id: "settings", label: "Site Settings", icon: Settings },
  ];

  return (
    <div className="flex flex-col md:flex-row gap-6">
      <div className="md:w-44 shrink-0 flex md:flex-col gap-1 overflow-x-auto pb-2 md:pb-0">
        {nav.map((n) => (
          <button key={n.id} onClick={() => setSection(n.id)} className={`whitespace-nowrap px-3 py-2.5 rounded-lg text-xs font-heading uppercase tracking-wider flex items-center gap-2 transition-all ${section === n.id ? "bg-gg-red text-white" : "text-zinc-500 hover:text-white hover:bg-white/5"}`}>
            <n.icon size={14} /> {n.label}
          </button>
        ))}
      </div>
      <div className="flex-1 space-y-4">
        {section === "hero" && (
          <Card title="Hero Section" icon={Layout} onSave={(silent) => save("hero", silent)} saving={saving === "hero"} onPublish={() => publish("hero")} publishing={publishing === "hero"} isDraft={meta.hero?.has_draft} scheduledAt={meta.hero?.scheduled_publish_at} onMetaUpdate={updateMeta} previewUrl="/" section="hero" token={token}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Badge Text" value={content.hero.badge} onChange={(v) => set("hero", "badge", v)} placeholder="e.g. 18+ Years" />
              <Field label="Primary Button" value={content.hero.cta_primary} onChange={(v) => set("hero", "cta_primary", v)} />
            </div>
            <Field label="Heading Line 1" value={content.hero.heading_line1} onChange={(v) => set("hero", "heading_line1", v)} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Heading Line 2 (red)" value={content.hero.heading_line2} onChange={(v) => set("hero", "heading_line2", v)} />
              <Field label="Heading Line 3" value={content.hero.heading_line3} onChange={(v) => set("hero", "heading_line3", v)} />
            </div>

            {/* Rotating additional taglines */}
            <div className="border border-white/5 rounded-lg p-4 bg-gg-black/40">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-heading text-xs uppercase tracking-wider text-white">Rotating Taglines</p>
                  <p className="text-zinc-600 text-[11px] mt-1">Additional headlines that cycle every 5s after the main one above. Leave empty to show only the main heading.</p>
                </div>
              </div>
              <div className="space-y-3 mt-3">
                {(content.hero.taglines || []).map((t, i) => (
                  <div key={i} className="border border-white/5 rounded-lg p-3 bg-gg-surface/40 space-y-2 relative" data-testid={`tagline-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-500 text-[10px] font-heading uppercase tracking-widest">Tagline {i + 1}</span>
                      <Trash2 size={12} className="text-zinc-600 hover:text-red-500 cursor-pointer" onClick={() => { const arr = [...(content.hero.taglines || [])]; arr.splice(i, 1); set("hero", "taglines", arr); }} />
                    </div>
                    <Field label="Line 1" value={t.line1} onChange={(v) => { const arr = [...(content.hero.taglines || [])]; arr[i] = { ...arr[i], line1: v }; set("hero", "taglines", arr); }} />
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Field label="Line 2 (red)" value={t.line2} onChange={(v) => { const arr = [...(content.hero.taglines || [])]; arr[i] = { ...arr[i], line2: v }; set("hero", "taglines", arr); }} />
                      <Field label="Line 3" value={t.line3} onChange={(v) => { const arr = [...(content.hero.taglines || [])]; arr[i] = { ...arr[i], line3: v }; set("hero", "taglines", arr); }} />
                    </div>
                  </div>
                ))}
                <Button size="sm" variant="outline" onClick={() => set("hero", "taglines", [...(content.hero.taglines || []), { line1: "", line2: "", line3: "" }])} className="rounded-lg border-dashed border-white/10 text-zinc-500 text-xs" data-testid="add-tagline-btn">
                  <Plus size={12} className="mr-1" /> Add Tagline
                </Button>
              </div>
            </div>

            <Field label="Subtitle" value={content.hero.subtitle} onChange={(v) => set("hero", "subtitle", v)} rows={2} />
            <Field label="Secondary Button" value={content.hero.cta_secondary} onChange={(v) => set("hero", "cta_secondary", v)} />
            <ImageUploader token={token} currentUrl={content.hero.image} onUploaded={(url) => set("hero", "image", url)} label="Hero Image — Desktop (16:9 landscape)" aspect={16/9} />
            <ImageUploader token={token} currentUrl={content.hero.image_mobile} onUploaded={(url) => set("hero", "image_mobile", url)} label="Hero Image — Mobile (9:16 portrait)" aspect={9/16} />
          </Card>
        )}
        {section === "about" && (
          <Card title="About Page" icon={FileText} onSave={(silent) => save("about", silent)} saving={saving === "about"} onPublish={() => publish("about")} publishing={publishing === "about"} isDraft={meta.about?.has_draft} scheduledAt={meta.about?.scheduled_publish_at} onMetaUpdate={updateMeta} previewUrl="/about" section="about" token={token}>
            <Field label="Your Name" value={content.about.name} onChange={(v) => set("about", "name", v)} />
            <RichTextEditor label="Bio / About Text" value={content.about.bio} onChange={(v) => set("about", "bio", v)} />
            <ImageUploader token={token} currentUrl={content.about.image} onUploaded={(url) => set("about", "image", url)} label="About Photo" aspect={4/5} />
          </Card>
        )}
        {section === "services" && (
          <Card title="Personal Training Services" icon={Dumbbell} onSave={(silent) => save("services", silent)} saving={saving === "services"} onPublish={() => publish("services")} publishing={publishing === "services"} isDraft={meta.services?.has_draft} scheduledAt={meta.services?.scheduled_publish_at} onMetaUpdate={updateMeta} previewUrl="/personal-training" section="services" token={token}>
            <p className="text-zinc-500 text-xs">Drag to reorder. Click to expand and edit.</p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd("services")}>
              <SortableContext items={content.services.map((_, i) => `services-${i}`)} strategy={verticalListSortingStrategy}>
                {content.services.map((s, i) => (<SortableServiceItem key={`services-${i}`} id={`services-${i}`} s={s} i={i} token={token} setArr={setArr} delArr={delArr} />))}
              </SortableContext>
            </DndContext>
            <Button onClick={() => addArr("services", { name: "", desc: "", detail: "", image: "" })} variant="outline" className="w-full rounded-lg border-dashed border-white/10 text-zinc-500 hover:text-white"><Plus size={14} className="mr-2" /> Add Service</Button>
          </Card>
        )}
        {section === "qualifications" && (
          <Card title="Qualifications & Certifications" icon={Star} onSave={() => save("qualifications")} saving={saving === "qualifications"} onPublish={() => publish("qualifications")} publishing={publishing === "qualifications"} isDraft={meta.qualifications?.has_draft} previewUrl="/qualifications" section="qualifications" token={token}>
            <p className="text-zinc-500 text-xs">Drag to reorder. Click to expand and edit.</p>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd("qualifications")}>
              <SortableContext items={content.qualifications.map((_, i) => `qualifications-${i}`)} strategy={verticalListSortingStrategy}>
                {content.qualifications.map((q, i) => (<SortableQualItem key={`qualifications-${i}`} id={`qualifications-${i}`} q={q} i={i} setArr={setArr} delArr={delArr} />))}
              </SortableContext>
            </DndContext>
            <Button onClick={() => addArr("qualifications", { title: "", desc: "", detail: "" })} variant="outline" className="w-full rounded-lg border-dashed border-white/10 text-zinc-500 hover:text-white"><Plus size={14} className="mr-2" /> Add Qualification</Button>
          </Card>
        )}
        {section === "nutrition" && (
          <Card title="Nutrition Page" icon={Leaf} onSave={(silent) => save("nutrition", silent)} saving={saving === "nutrition"} onPublish={() => publish("nutrition")} publishing={publishing === "nutrition"} isDraft={meta.nutrition?.has_draft} scheduledAt={meta.nutrition?.scheduled_publish_at} onMetaUpdate={updateMeta} previewUrl="/nutrition" section="nutrition" token={token}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Page Title" value={content.nutrition.title} onChange={(v) => set("nutrition", "title", v)} />
              <Field label="Subtitle" value={content.nutrition.subtitle} onChange={(v) => set("nutrition", "subtitle", v)} />
            </div>
            <Field label="Description" value={content.nutrition.description} onChange={(v) => set("nutrition", "description", v)} rows={3} />
            <div>
              <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">Features Bullet Points</label>
              {content.nutrition.features.map((f, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <Input value={f} onChange={(e) => { const arr = [...content.nutrition.features]; arr[i] = e.target.value; set("nutrition", "features", arr); }} className="bg-gg-black border-white/10 text-white rounded-lg text-sm h-9 flex-1" />
                  <Button size="sm" variant="ghost" onClick={() => { set("nutrition", "features", content.nutrition.features.filter((_, j) => j !== i)); }} className="text-zinc-600 hover:text-red-500 shrink-0"><Trash2 size={12} /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => set("nutrition", "features", [...content.nutrition.features, ""])} className="rounded-lg border-dashed border-white/10 text-zinc-500 text-xs"><Plus size={12} className="mr-1" /> Add</Button>
            </div>
            <ImageUploader token={token} currentUrl={content.nutrition.image} onUploaded={(url) => set("nutrition", "image", url)} label="Nutrition Page Image" aspect={4/3} />
          </Card>
        )}
        {section === "online_training" && (
          <Card title="Online Training Page" icon={Globe} onSave={(silent) => save("online_training", silent)} saving={saving === "online_training"} onPublish={() => publish("online_training")} publishing={publishing === "online_training"} isDraft={meta.online_training?.has_draft} scheduledAt={meta.online_training?.scheduled_publish_at} onMetaUpdate={updateMeta} previewUrl="/online-training" section="online_training" token={token}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Page Title" value={content.online_training.title} onChange={(v) => set("online_training", "title", v)} />
              <Field label="Subtitle" value={content.online_training.subtitle} onChange={(v) => set("online_training", "subtitle", v)} />
            </div>
            <Field label="Description" value={content.online_training.description} onChange={(v) => set("online_training", "description", v)} rows={3} />
            <ImageUploader token={token} currentUrl={content.online_training.image} onUploaded={(url) => set("online_training", "image", url)} label="Online Training Image (16:9 recommended — appears above the CTA card)" aspect={16/9} />
            <div>
              <label className="text-zinc-500 text-xs uppercase tracking-wider mb-2 block">Features</label>
              {content.online_training.features.map((f, i) => (
                <div key={i} className="border border-white/5 rounded-lg p-3 mb-2 space-y-2">
                  <Field label={`Feature ${i + 1} Title`} value={f.title} onChange={(v) => { const arr = [...content.online_training.features]; arr[i] = { ...arr[i], title: v }; set("online_training", "features", arr); }} />
                  <Field label="Description" value={f.desc} onChange={(v) => { const arr = [...content.online_training.features]; arr[i] = { ...arr[i], desc: v }; set("online_training", "features", arr); }} rows={2} />
                </div>
              ))}
            </div>
          </Card>
        )}
        {section === "settings" && (
          <Card title="Site Settings" icon={Settings} onSave={(silent) => save("settings", silent)} saving={saving === "settings"} onPublish={() => publish("settings")} publishing={publishing === "settings"} isDraft={meta.settings?.has_draft} scheduledAt={meta.settings?.scheduled_publish_at} onMetaUpdate={updateMeta} previewUrl="/" section="settings" token={token}>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-end gap-2">
                <div className="flex-1"><Field label="Phone Number" value={content.settings.phone} onChange={(v) => set("settings", "phone", v)} /></div>
                <Phone size={16} className="text-zinc-600 mb-3" />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1"><Field label="WhatsApp (with country code)" value={content.settings.whatsapp} onChange={(v) => set("settings", "whatsapp", v)} /></div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-end gap-2">
                <div className="flex-1"><Field label="Location Line 1" value={content.settings.location_line1} onChange={(v) => set("settings", "location_line1", v)} /></div>
                <MapPin size={16} className="text-zinc-600 mb-3" />
              </div>
              <Field label="Location Line 2" value={content.settings.location_line2} onChange={(v) => set("settings", "location_line2", v)} />
            </div>
            <Field label="Google Maps Search Query" value={content.settings.maps_query} onChange={(v) => set("settings", "maps_query", v)} placeholder="e.g. Bolpur+Santiniketan+West+Bengal" />
            <hr className="border-white/5" />
            <p className="text-zinc-500 text-xs uppercase tracking-wider">Social Media Links</p>
            <Field label="Facebook URL" value={content.settings.facebook} onChange={(v) => set("settings", "facebook", v)} />
            <Field label="Instagram URL" value={content.settings.instagram} onChange={(v) => set("settings", "instagram", v)} />
            <Field label="YouTube URL" value={content.settings.youtube} onChange={(v) => set("settings", "youtube", v)} />
            <hr className="border-white/5" />
            <ImageUploader token={token} currentUrl={content.settings.logo} onUploaded={(url) => set("settings", "logo", url)} label="Site Logo" />
          </Card>
        )}
      </div>
    </div>
  );
}
